import React, { useState } from 'react';
import { Col, Row, Container, Table, Input } from 'reactstrap';
import { AccentButton } from "../controls/AccentButton";
import { AccentSpinner } from "../controls/AccentSpinner";
import { AccentRadioUnboundCustom } from '../controls/unbound/AccentRadioUnbound';
import { AccentComboUnbound } from '../controls/unbound/AccentComboUnbound';
import { AccentTextAreaUnbound } from '../controls/unbound/AccentTextAreaUnbound';
import { update, useQueryAllNoTracking } from "../services/DataService";
import { accentUtils, from, showDialog, t } from "../services/HelperService";
import { jobLineStages } from '../services/JobService';
import { AccentDurationUnbound } from '../controls/unbound/AccentDurationUnbound';
import { AccentDialog } from "./AccentDialog";
import {
    addMinutes,
    alignEndToStart,
    fromActivityTimeZone,
    getDurationMinutes,
    MAX_DURATION_MINUTES,
    toActivityTimeZone,
    toTimeValue,
    updateDateTime
} from './EditTimesDlg';


const CopyToDlg = ({ onClose, lines = [], sourceIndex }) => {
    const [sameOutcomeOnly, setSameOutcomeOnly] = React.useState(false); // leave OFF by default
    const [selected, setSelected] = React.useState({}); // { [index]: true }

    const source = lines[sourceIndex];
    const sourceOutcome = source?.Status ?? 'Completed';

    const eligibleIndexes = lines
        .map((l, i) => i)
        .filter(i => i !== sourceIndex)
        // pretend Unconfirmed_CM lines are not there
        .filter(i => (lines[i]?.Stage !== jobLineStages.Unconfirmed_CM))
        .filter(i => !sameOutcomeOnly || ((lines[i]?.Status ?? 'Completed') === sourceOutcome));

    const toggle = (i) => setSelected(prev => ({ ...prev, [i]: !prev[i] }));

    const selectAll = (e) => {
        const next = {};
        eligibleIndexes.forEach(i => { next[i] = true; });
        setSelected(next);
        e?.promise?.();
    };

    const clearAll = (e) => {
        setSelected({});
        e?.promise?.();
    };

    const doCopy = (e) => {
        const targetIndexes = eligibleIndexes.filter(i => !!selected[i]);
        onClose({ canceled: false, targetIndexes });
        e?.promise?.();
    };

    return (
        <AccentDialog
            headerContent="Copy values to lines"
            onClose={() => onClose({ canceled: true })}
            relativeWidth={d => Math.min(d.width - 80, 900)}
            actionsContent={[
                <AccentButton key="copy" outline={false} onClick={doCopy}>COPY</AccentButton>,
            ]}
        >
            <div className="mb-2">
                <strong>Source:</strong>{" "}
                {source?.JobLineNo} - {source?.LocationOther}: {source?.ProductName}
            </div>

            <Row className="mb-2">
                <Col xs="12" sm="6">
                    <label className="me-2"><strong>Same outcome only</strong></label>
                    <Input
                        type="checkbox"
                        checked={sameOutcomeOnly}
                        onChange={(e) => { setSameOutcomeOnly(e.target.checked); setSelected({}); }}
                    />
                </Col>
                <Col xs="12" sm="6" className="d-flex justify-content-end gap-2">
                    <AccentButton outline={false} onClick={selectAll}>SELECT ALL</AccentButton>
                    <AccentButton outline={false} onClick={clearAll}>CLEAR ALL</AccentButton>
                </Col>
            </Row>

            {eligibleIndexes.length === 0 && (
                <div>No eligible lines available.</div>
            )}

            {eligibleIndexes.length > 0 && (
                <div>
                    {eligibleIndexes.map(i => {
                        const l = lines[i];
                        return (
                            <div key={i} className="d-flex align-items-start mb-2">
                                <Input
                                    type="checkbox"
                                    checked={!!selected[i]}
                                    onChange={() => toggle(i)}
                                    style={{ marginTop: 4 }}
                                />
                                <div style={{ marginLeft: 10 }}>
                                    <strong>
                                        {l.JobLineNo} - {l.LocationOther}: {l.ProductName}
                                    </strong>
                                    <div className="text-muted">
                                        Status: <strong>{l.Status ?? 'Completed'}</strong>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </AccentDialog>
    );
};

const JobItemsList = ({ jobItems, rescheduleReasons, onChange }) => {
    const [items, setItems] = useState(
        jobItems.map((item) => ({
            ...item,
            Status: 'Completed',
            RescheduleReasonID: null,
            RescheduleNotes: null
        }))
    );

    // Only bump this when we need to force remount of "unbound" controls (copy),
    // not on every keystroke (otherwise notes loses focus).
    const [remountKey, setRemountKey] = React.useState(1);

    const canReschedule = (item) => item.Stage !== jobLineStages.Unconfirmed_CM;

    const pushChange = (updated) => {
        setItems(updated);
        onChange(updated);
    };

    const handleStatusChange = (itemID, newStatus) => {
        const updatedItems = items.map((item) => {
            if (item.ID !== itemID) return item;

            if (newStatus === 'Completed') {
                return { ...item, Status: 'Completed', RescheduleReasonID: null, RescheduleNotes: null };
            }

            return { ...item, Status: 'Reschedule' };
        });

        pushChange(updatedItems);
    };

    const handleReasonChange = (itemID, reasonID) => {
        const updated = items.map((item) =>
            item.ID === itemID ? { ...item, RescheduleReasonID: reasonID } : item
        );
        pushChange(updated);
    };

    const handleNotesChange = (itemID, notes) => {
        const updated = items.map((item) =>
            item.ID === itemID ? { ...item, RescheduleNotes: notes } : item
        );
        pushChange(updated);
    };

    const applyFromSourceToTargets = (sourceIndex, targetIndexes = null) => {
        const source = items[sourceIndex];
        if (!source) return;

        const sourceStatus = canReschedule(source) ? (source.Status ?? 'Completed') : 'Completed';

        const targetSet = (targetIndexes === null)
            ? new Set(items.map((_, i) => i).filter(i => i !== sourceIndex))
            : new Set(targetIndexes.filter(i => i !== sourceIndex));

        const updated = items.map((line, i) => {
            if (!targetSet.has(i)) return line;

            // Pretend Unconfirmed_CM lines are not there and force complete.
            if (!canReschedule(line)) {
                return {
                    ...line,
                    Status: 'Completed',
                    RescheduleReasonID: null,
                    RescheduleNotes: null
                };
            }

            // Always override
            if (sourceStatus === 'Completed') {
                return {
                    ...line,
                    Status: 'Completed',
                    RescheduleReasonID: null,
                    RescheduleNotes: null
                };
            }

            // Reschedule
            return {
                ...line,
                Status: 'Reschedule',
                RescheduleReasonID: source.RescheduleReasonID,
                RescheduleNotes: source.RescheduleNotes
            };
        });

        pushChange(updated);
        setRemountKey(x => x + 1);
    };

    const onCopyToAll = (sourceIndex) => applyFromSourceToTargets(sourceIndex, null);

    const onCopyTo = async (sourceIndex) => {
        const eligibleCount = items.filter(i => canReschedule(i)).length;
        if (eligibleCount <= 1) return;

        const result = await showDialog(<CopyToDlg lines={items} sourceIndex={sourceIndex} />);
        if (result?.canceled) return;

        const targets = result?.targetIndexes ?? [];
        if (targets.length === 0) return;

        applyFromSourceToTargets(sourceIndex, targets);
    };

    const showCopyButtons = items.filter(i => canReschedule(i)).length > 1;

    return (
        <Container>
            <Row>
                <Col>
                    <Table striped bordered hover responsive>
                        <thead>
                            <tr>
                                <th>Job Line No</th>
                                <th>Location</th>
                                <th>Product Name</th>
                                <th>Stage</th>
                                <th>Status</th>
                                <th style={{ width: "1%" }}>Copy</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item, idx) => {
                                const allowReschedule = canReschedule(item);
                                const isReschedule = allowReschedule && (item.Status ?? 'Completed') === 'Reschedule';

                                return (
                                    <React.Fragment key={item.ID}>
                                        <tr>
                                            <td>{item.JobLineNo}</td>
                                            <td>{item.LocationOther}</td>
                                            <td>{item.ProductName}</td>
                                            <td>{t(item.Stage)}</td>
                                            <td>
                                                <div>
                                                    <AccentRadioUnboundCustom
                                                        key={`${remountKey}_${item.ID}_Completed_${item.Status ?? 'Completed'}`}
                                                        checked={(item.Status ?? 'Completed') === 'Completed'}
                                                        onChange={() => handleStatusChange(item.ID, 'Completed')}
                                                        label="Completed"
                                                        name={`statusGroup${item.ID}`}
                                                    />
                                                    {allowReschedule && (
                                                        <AccentRadioUnboundCustom
                                                            key={`${remountKey}_${item.ID}_Reschedule_${item.Status ?? 'Completed'}`}
                                                            checked={(item.Status ?? 'Completed') === 'Reschedule'}
                                                            onChange={() => handleStatusChange(item.ID, 'Reschedule')}
                                                            label="Reschedule"
                                                            name={`statusGroup${item.ID}`}
                                                        />
                                                    )}
                                                </div>
                                            </td>
                                            <td className="align-middle text-nowrap" style={{ minWidth: 260 }}>
                                                {showCopyButtons && allowReschedule && (
                                                    <div className="d-flex gap-2">
                                                        <AccentButton outline={false} onClick={() => onCopyToAll(idx)}>COPY TO ALL</AccentButton>
                                                        <AccentButton outline={false} onClick={() => onCopyTo(idx)}>COPY TO</AccentButton>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>

                                        {isReschedule && (
                                            <tr>
                                                <td colSpan={6}>
                                                    <div style={{ paddingLeft: 18 }}>
                                                        <div key={`${remountKey}_reason_${item.ID}`}>
                                                            <AccentComboUnbound
                                                                label="Reason"
                                                                displayField="Description"
                                                                valueField="ID"
                                                                required
                                                                defaultValue={item.RescheduleReasonID}
                                                                onChange={(v) => handleReasonChange(item.ID, v)}
                                                                data={rescheduleReasons}
                                                            />
                                                        </div>
                                                        <div key={`${remountKey}_notes_${item.ID}`}>
                                                            <AccentTextAreaUnbound
                                                                label="Notes"
                                                                defaultValue={item.RescheduleNotes}
                                                                onChange={(v) => handleNotesChange(item.ID, v)}
                                                                rows={2}
                                                            />
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </Table>
                </Col>
            </Row>
        </Container>
    );
};


export const CMCompleteDlg = props => {

    const args = React.useRef({ id: props.jobID });
    const argsReschedule = React.useRef({ includeID: null, active: true });

    const lines = useQueryAllNoTracking("Job_CompleteCMLines", args.current);
    const reasons = useQueryAllNoTracking("PL_ActivityRescheduleReasons_SelectList", argsReschedule.current);
    const times = useQueryAllNoTracking("Job_CompleteCMTimes", args.current);

    const [cmLines, setCMLines] = React.useState([]);
    const [timeRows, setTimeRows] = React.useState(null);

    const timesInfo = times.loading ? null : (times.data?.[0] ?? null);
    const timeZone = timesInfo?.TimeZone;
    const appointmentStart = React.useMemo(
        () => toActivityTimeZone(timesInfo?.AppointmentStart, timeZone),
        [timesInfo?.AppointmentStart, timeZone]
    );

    React.useEffect(() => {
        if (!timesInfo?.ActivityID) return;

        const completedAt = toActivityTimeZone(new Date(), timeZone);

        setTimeRows((timesInfo.Attendees ?? []).map(attendee => {
            const savedTime = (timesInfo.ActivityTimes ?? []).find(time => time.RepID === attendee.RepID);
            const start = toActivityTimeZone(savedTime?.Start, timeZone) ?? appointmentStart;
            const savedEnd = toActivityTimeZone(savedTime?.End, timeZone);
            const end = savedEnd ? alignEndToStart(start, savedEnd) : completedAt;
            return {
                RepID: attendee.RepID,
                Name: attendee.Name,
                Start: start,
                End: end,
                DurationMinutes: getDurationMinutes(start, end),
                DurationIsManual: false
            };
        }));
    }, [timesInfo, timeZone, appointmentStart]);

    const updateTimeRow = (repID, updater) => {
        setTimeRows(prev => prev.map(row => row.RepID === repID ? updater(row) : row));
    };

    const updateStart = (repID, value) => updateTimeRow(repID, row => {
        if (row.DurationIsManual && row.DurationMinutes !== null) {
            return {
                ...row,
                Start: value,
                End: addMinutes(value ?? appointmentStart, row.DurationMinutes)
            };
        }

        const effectiveStart = value ?? appointmentStart;
        const end = alignEndToStart(effectiveStart, row.End);
        return {
            ...row,
            Start: value,
            End: end,
            DurationMinutes: getDurationMinutes(effectiveStart, end)
        };
    });

    const updateEnd = (repID, value) => updateTimeRow(repID, row => {
        const start = row.Start ?? appointmentStart;
        const end = alignEndToStart(start, value);
        return {
            ...row,
            End: end,
            DurationMinutes: getDurationMinutes(start, end),
            DurationIsManual: false
        };
    });

    const updateDuration = (repID, durationMinutes) => updateTimeRow(repID, row => {
        if (durationMinutes === null || !Number.isFinite(durationMinutes)) {
            return {
                ...row,
                DurationMinutes: null,
                DurationIsManual: false
            };
        }

        return {
            ...row,
            DurationMinutes: durationMinutes,
            DurationIsManual: true,
            End: addMinutes(row.Start ?? appointmentStart, durationMinutes)
        };
    });

    const copyTimeToAll = (repID, e) => {
        setTimeRows(prev => {
            const source = prev.find(row => row.RepID === repID);
            if (!source) return prev;

            return prev.map(row => ({
                ...row,
                Start: source.Start,
                End: source.End,
                DurationMinutes: source.DurationMinutes,
                DurationIsManual: source.DurationIsManual
            }));
        });
        e?.promise?.();
    };

    const allValid = React.useMemo(() => {
        const list = cmLines ?? [];
        return !list.some(l =>
            l.Stage !== jobLineStages.Unconfirmed_CM
            && l.Status === 'Reschedule'
            && accentUtils.isEmpty(l.RescheduleReasonID)
        );
    }, [cmLines]);

    const onOk = async e => {
        if (timesInfo?.ActivityID && timeRows?.length) {
            await update(
                "Activity_UpdateTimes",
                {
                    ID: timesInfo.ActivityID,
                    Times: timeRows.map(({ RepID, Start, End }) => ({
                        RepID,
                        Start: fromActivityTimeZone(Start, timeZone),
                        End: fromActivityTimeZone(End, timeZone)
                    }))
                },
                true
            );
        }

        props.onClose({
            canceled: false,
            cmLines: (cmLines ?? []).map(l => ({
                JobLineID: l.ID,
                Outcome: (l.Stage === jobLineStages.Unconfirmed_CM) ? 'Completed' : (l.Status ?? 'Completed'),
                RescheduleReasonID: (l.Stage === jobLineStages.Unconfirmed_CM || (l.Status ?? 'Completed') === 'Completed') ? null : l.RescheduleReasonID,
                RescheduleNotes: (l.Stage === jobLineStages.Unconfirmed_CM || (l.Status ?? 'Completed') === 'Completed') ? null : l.RescheduleNotes
            }))
        });

        e?.promise?.();
    };

    const onChange = e => {
        setCMLines(e);
    };


    const timesSection = !timesInfo?.ActivityID ? null : (
        <Container className="mb-3">
            <Row>
                <Col>
                    <Table striped bordered hover responsive>
                        <thead>
                            <tr>
                                <th>Attendee</th>
                                <th>Start Time</th>
                                <th>End Time</th>
                                <th>Duration</th>
                                <th style={{ width: "1%" }}>Copy</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(timeRows ?? []).map(row => (
                                <tr key={row.RepID}>
                                    <td className="align-middle">{row.Name}</td>
                                    <td className="align-middle">
                                        <Input
                                            type="time"
                                            value={toTimeValue(row.Start) ?? ""}
                                            onChange={e => updateStart(row.RepID, updateDateTime(row.Start ?? appointmentStart, e.target.value))}
                                        />
                                    </td>
                                    <td className="align-middle">
                                        <Input
                                            type="time"
                                            value={toTimeValue(row.End) ?? ""}
                                            onChange={e => updateEnd(row.RepID, updateDateTime(row.End ?? row.Start ?? appointmentStart, e.target.value))}
                                        />
                                    </td>
                                    <td className="align-middle">
                                        <AccentDurationUnbound
                                            key={`${row.RepID}_duration`}
                                            defaultValue={row.DurationMinutes}
                                            maxMinutes={MAX_DURATION_MINUTES}
                                            onChange={value => updateDuration(row.RepID, value)}
                                        />
                                    </td>
                                    <td className="align-middle text-nowrap" style={{ minWidth: 160 }}>
                                        {(timeRows?.length ?? 0) > 1 && (
                                            <AccentButton outline={false} onClick={e => copyTimeToAll(row.RepID, e)}>COPY TO ALL</AccentButton>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Col>
            </Row>
        </Container>
    );

    let content = null;

    if (lines.loading || reasons.loading || times.loading) {
        content = <AccentSpinner />;
    } else {
        content = (
            <React.Fragment>
                {timesSection}
                <JobItemsList jobItems={lines.data} rescheduleReasons={reasons.data ?? []} onChange={onChange} />
            </React.Fragment>
        );
    }


    return <AccentDialog        
        onClose={props.onClose}
        headerContent={t("application_strings.application.dialogs.completeCMDlg.title")}
        actionsContent={[
            <AccentButton key="1" tagName="completeCMDlgClose" onClick={onOk} disabled={!allValid}>{t("application_strings.application.buttons.ok")}</AccentButton>
        ]}
    >
        { content}
    </AccentDialog>;

};